import React from 'react';
import { NextSeo } from 'next-seo';
import Head from 'next/head';
import { Layout } from 'components/Layout';
import { getPageStaticProps } from 'lib/dato-request';
import ParentsHero from '@/components/Parents/ParentsHero';
import VideoFlex from '@/components/Parents/VideoFlex';
import VideoFlex2 from '@/components/Parents/VideoFlex2';
import VideoFlex3 from '@/components/Parents/VideoFlex3';
import ExploreMath from '@/components/ExploreMath/ExploreMath';
import ThreeStepChild from '@/components/Parents/ThreeStepChild';
import FounderSection from '@/components/Parents/FounderSection';
import MailingList from '@/components/MailingList/MailingList';
import ParentsCTA from '@/components/Parents/ParentsCTA';
import { Trustbar2 } from '@/components/Trustbar2';
import ImageBarSection from '@/components/Trustbar2/ImageBarSection';
import TestimonialGrid from '@/components/TestimonialCarousel/TestimonialGrid';
import ContentSection from "@/components/ParentsLandingPage/ContentSection";
import ContentSection2 from "@/components/ParentsLandingPage/ContentSection2";
import ContentSection3 from "@/components/ParentsLandingPage/ContentSection3";
import FounderSection2 from "@/components/ParentsLandingPage/FounderSection2";
import { MediumHeading } from "@/components/Views/Heading";
import { Accordion } from "@/components/FAQ/Accordion";
import MinusIcon from "@/assets/icons/minus-purple.svg";
import PlusIcon from "@/assets/icons/plus-purple.svg";
import TestimonialListSimple from "@/components/ParentsLandingPage/TestimonialListSimple";
import { floatingIcon } from "@/components/Animations/IconBlock";
import CustomNavbar from "@/components/ParentsLandingPage/CustomNavbar";

const pricingURL = '/rockers-signup';

const ParentsPage = ({ data }) => {
    const { page } = data;
    const { components, seo } = page;

    const heroDesktopFloatingIconsPositions = [floatingIcon('75%', '18%', '/icons/float-icon-blue-func.svg', 'w-20 md:w-24 lg:w-26 xl:w-28', 0, 1.2), floatingIcon('-2%', '6%', '/icons/float-icon-orange-note.svg', 'w-14 md:w-18 lg:w-18 xl:w-20', 0, 1.4), floatingIcon('18%', '80%', '/icons/float-icon-white-pi.svg', 'w-14 md:w-12 lg:w-26 xl:w-28', 0, 1.5), floatingIcon('90%', '60%', '/icons/floating-icon-green-treble.png', 'w-14 md:w-12 lg:w-26 xl:w-28', 15, 1.5), floatingIcon('55%', '95%', '/icons/float-icon-orange-infinity.png', 'w-14 md:w-12 lg:w-26 xl:w-28', 15, 1.0)];
    const heroMobileFloatingIconsPositions = [floatingIcon('74%', '18%', '/icons/float-icon-blue-func.svg', 'w-20 md:w-24 lg:w-26 xl:w-28', 0, 1.4), floatingIcon('2%', '6%', '/icons/float-icon-orange-note.svg', 'w-14 md:w-18 lg:w-18 xl:w-20', 0, 1.6), floatingIcon('18%', '80%', '/icons/float-icon-white-pi.svg', 'w-14 md:w-12 lg:w-26 xl:w-28', 0, 1.8)];

    return (
        <Layout
            navbarComponent={(
                <CustomNavbar
                    id='rockers-landing-navbar'
                    pricingURL={pricingURL}
                    pricingCTA='START FREE TRIAL'
                    pricingClassName='purple-button'
                />
            )}
            navbarComponentMobile={(
                <CustomNavbar
                    id='rockers-landing-navbar'
                    isMobile={true}
                    pricingURL={pricingURL}
                    pricingCTA='START FREE TRIAL'
                    pricingClassName='purple-button'
                />
            )}
        >

            <NextSeo title={seo?.title} description={seo?.description} />
            <Head>
                <meta name='keywords' content={page.keywords} />
            </Head>

            <ParentsHero
                id='parents-hero'
                heading={components[0].heading}
                content={components[0].content}
                featuredImage={components[0].featuredImage}
                subhead={components[0].subhead}
                ctaURL={pricingURL}
                buttonText='START FREE TRIAL'
                desktopFloatingIcons={heroDesktopFloatingIconsPositions}
                mobileFloatingIcons={heroMobileFloatingIconsPositions}
                bannerText={components[0].bannerText}
            />

            <ContentSection
                id='parents-landing-section-1'
                component={components[1]}
                className='bg-gradient-to-b from-[#E0DDFF] to-[#FFFFFF00]'
                pricingURL={pricingURL}
                ctaSignupButtonClass='purple-button'
                ctaSignupButton='SIGN UP NOW'
                videoButtonText='SEE HOW IT WORKS'
                textClass='text-[#4B5563]'
            />

            <ContentSection2
                id='parents-landing-section-2'
                component={components[2]}
                pricingURL={pricingURL}
                ctaSignupButtonClass='purple-button'
                ctaSignupButton='SIGN UP NOW'
                removeVideoButton={true}
            />

            <ContentSection3
                id='parents-landing-section-3'
                component={components[3]}
                background='linear-gradient(180deg, rgba(255, 255, 255, 0.00) 0%, #E0DDFF 100%)'
                pricingURL={pricingURL}
                ctaSignupButtonClass='purple-button'
                ctaSignupButton='SIGN UP NOW'
                subText='Try Muzology free with your child today!'
            />

            <FounderSection2
                id='parents-landing-founder-section'
                component={components[4]}
                background='linear-gradient(180deg, #E0DDFF 0%, #F9F8FF 100%)'
                className='lg:mt-20 mb-[60px]'
                ctaButtonText='SEE HOW IT WORKS'
                playButtonClass='orange-pink-play-btn'
            />

            <div className='pb-10 pt-10'>
                <div className='default-max-w mx-auto md:max-w-[940px] '>
                    <MediumHeading className='font-extrabold text-center text-4xl leading-none !text-MuzologyDarkPurple text-[#363535] mt-5'>
                        FAQ
                    </MediumHeading>
                </div>
                <div className='default-px'>
                    <div className='default-max-w mx-auto md:max-w-[940px] '>
                        <Accordion
                            minusIcon={<MinusIcon />}
                            plusIcon={<PlusIcon />}
                            // id='parents-landing-faq'
                            faqs={components[5].faqQuestion}
                            className='mx-auto'
                            style={{
                                paddingTop: 10, paddingBottom: 10, background: '#ffffff'
                            }}
                        />
                    </div>
                </div>
            </div>

            <div className='pb-10 pt-4'>
                {/*
                <div className='default-max-w mx-auto md:max-w-[940px] '>
                    <MediumHeading className='font-extrabold text-center text-4xl leading-none text-[#363535] !text-MuzologyDarkPurple'>
                        Hear from Some of our Fans!
                    </MediumHeading>
                </div>
                */}
                <TestimonialListSimple sliceNumber={3} testimonials={components[6].testimonials} />
            </div>

        </Layout>
    );
};

export async function getStaticProps() {
    return getPageStaticProps('Rockers Landing Page');
}

export default ParentsPage;
